import { MDBCheckbox, MDBInput, MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CustomSelect } from "../../../CustomSelect";
import { DayPicker } from "react-day-picker";
import TimePicker from "react-time-picker";
import { ASParamEditorProps } from "../ASParamEditor";

const WEEK_DAYS = [
    "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday", "Holidays"
]

export const ASScheduleParam = ({currentValue, realValue, onCurrentValueChange, disabled}: ASParamEditorProps<number[]>) => {
    const [selectedScheduleIdx, setSelectedScheduleIdx] = useState<null|number>(null);

    const selectedSchedule = useMemo(() => {
        const value = (currentValue ?? [])[selectedScheduleIdx ?? -1] ?? 0;

        return {
            idx: selectedScheduleIdx,
            value,
            title: scheduleTitle(selectedScheduleIdx ?? -1, value),
            onOffStatusValue: scheduleOnOffValue(value),
            timeString: scheduleTimeString(value)
        }

    }, [selectedScheduleIdx, currentValue]);

    const onDayCheck = useCallback((dayIdx: number, check: boolean) => {
        const newVal = setNthBit(selectedSchedule.value, 24+dayIdx, check);

        onCurrentValueChange((currentValue ?? []).map((sch, idx) => idx !== selectedSchedule.idx ? sch : newVal));
    }, [selectedSchedule, currentValue, onCurrentValueChange]);

    const onTimeSelect = useCallback((str: string) => {
        if (!str) return;
        const time = Number(str.split(':')[0])*60 + Number(str.split(':')[1]);

        const newVal = (selectedSchedule.value & 0xFFFF0000) | time;

        onCurrentValueChange((currentValue ?? []).map((sch, idx) => idx !== selectedSchedule.idx ? sch : newVal));
    }, [selectedSchedule, currentValue, onCurrentValueChange]);

    const onScheduleOnOffValue = useCallback((on: boolean) => {
        const newVal = on ? ((selectedSchedule.value & 0xFF00FFFF) | 0x00010000) : (selectedSchedule.value & 0xFF00FFFF);
        onCurrentValueChange((currentValue ?? []).map((sch, idx) => idx !== selectedSchedule.idx ? sch : newVal));
    }, [selectedSchedule, currentValue, onCurrentValueChange]);

    useEffect(() => {
        if (currentValue !== null && currentValue.length > 0 && selectedScheduleIdx == null) setSelectedScheduleIdx(0);
    }, [currentValue, selectedScheduleIdx]);

    return (
        <div>
            <CustomSelect
                items={(currentValue ?? []).map((val, idx) => ({
                    val: idx,
                    title: scheduleTitle(idx, val)
                }))}
                defaultTitle="No data"
                selected={selectedScheduleIdx}
                disabled={disabled}
                onSelect={val => setSelectedScheduleIdx(Number(val))}
                width={350}
            />
            {selectedScheduleIdx !== null &&
                <div className="d-flex flex-row justify-content-start" style={{width: 350}}>
                    <div className="d-flex flex-column justify-content-start align-items-start mt-2" style={{flex: 1}}>
                        {WEEK_DAYS.map((day, idx) => (
                            <MDBCheckbox
                                key={day}
                                type="checkbox"
                                className="ms-1 ps-0"
                                checked={Boolean(((selectedSchedule.value & (1 << (24+idx)))))}
                                disabled={disabled}
                                onChange={ev => onDayCheck(idx, ev.currentTarget.checked)}
                                label={day}
                                labelStyle={{lineHeight: 1.4}}
                                wrapperClass="p-0"
                            />
                        ))}
                    </div>
                    <div className="d-flex flex-column" style={{flex: 1}}>
                        <TimePicker
                            value={selectedSchedule.timeString}
                            format="HH:mm"
                            clearIcon={null}
                            clockIcon={null}
                            disableClock={true}
                            onChange={t => onTimeSelect(t?.toString() ?? "")}
                            
                        />
                        <span>
                            <CustomSelect
                                items={[{val: 0, title: 'Off'}, {val: 1, title: 'On'}]}
                                selected={selectedSchedule.onOffStatusValue}
                                onSelect={val => onScheduleOnOffValue(Boolean(val))}
                            />
                        </span>
                    </div>
                </div>
            }
        </div>
    )
}


const scheduleOnOffValue = (value: number): 1|0 => {
    return ((value >> 16) & 0x01) == 1 ? 1 : 0;
}

const scheduleTimeString = (value: number): string => {
    let clock = value & 0xFFFF;
    if (clock >= 1440) {
        console.error(`Invalid clock value: ` + clock);
        clock = 0;
    }
    const hours = Math.floor(clock/60),
    mins = clock - hours*60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

const scheduleActivatedDays = (value: number): string[] => {
    return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su', 'Ho']
        .filter((_, idx) => value & (1 << (24+idx)));
}

const scheduleTitle = (idx: number, value: number) => {
    const on = scheduleOnOffValue(value) == 1 ? "ON" : "OFF";
    const time = scheduleTimeString(value);
    const days = scheduleActivatedDays(value).join('') || "N/A";

    return `#${idx} ${on} - ${time} ${days}`;
}

const setNthBit = (val: number, bit: number, set: boolean) => {
    return set ? (val | (1 << bit)) : (val & (~(1 << bit)));
}